import { Component, Mixins, Watch } from 'vue-property-decorator';
import { MagicConfigMixin } from '@gig/magic-common';
import {
    notificationService,
    NotificationDelay,
    NotificationStyle,
} from '@gig/magic-messaging';

import { magic } from '@gig/magic-sdk';

import { RouteName } from '../../routes/routes.constants';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import {
    ACCOUNT_IS_PROCESSING_STATUS_QUERY,
    ACCOUNT_REQUIRES_VERIFICATION_UPDATED,
    ACCOUNT_IS_VERIFIED_NOTIFICATION,
    ACCOUNT_IS_VERIFIEDPARTIAL_NOTIFICATION,
    USER_PROFILE_DATA,
    CLOSE_GAME,
} from '@gig/magic-messages';
import { ISubscription } from '@gig/magic-message-bus/dist/base-bus';
import {
    CorexUserRestriction,
    getRestrictionCheck,
    isModalOpen,
    openModal,
} from '../../services/routing/guards/user-restrictions.guard';
import type { ISubscription as IMagicSubscription } from '@gig/magic-sdk/dist/events/pubsub';
import { IUserProfile } from '@gig/magic-sdk/dist/adapter/users/user';
import { WalletViewRouteName } from '@gig/magic-wallet-types';
import { config } from '../../services';

@Component({
    name: 'block-manager',
})
export default class BlockManagerComponent extends Mixins(MagicConfigMixin) {
    private blockStatusSubscription: ISubscription | null = null;
    private userProfileUpdatedSubscription: IMagicSubscription | undefined =
        undefined;

    render() {
        return null;
    }

    @Watch('$route.name')
    onRouteChange() {
        if (this.$route.name !== null && !this.blockStatusSubscription) {
            this.initBlockStatusSubscription();
        }
    }

    routeUser(country: Country | undefined) {
        switch (country) {
            case Country.NETHERLANDS:
            case Country.ARGENTINA:
            case Country.ROMANIA:
                if (this.$route.name !== RouteName.USER_VERIFICATION) {
                    this.$router.push({
                        name: RouteName.USER_VERIFICATION,
                    });
                }
                break;
            default:
                if (this.$route.name !== RouteName.VERIFICATION) {
                    this.$router.push({
                        name: RouteName.VERIFICATION,
                    });
                }
        }
    }

    async initBlockStatusSubscription() {
        this.blockStatusSubscription = window.eventBus.subscribe<boolean>(
            ACCOUNT_REQUIRES_VERIFICATION_UPDATED,
            async (e) => {
                if (!e.payload) {
                    return;
                }
                const user = magic.session.getUser();

                this.routeUser(user?.country);

                const isProcessing = await window.queryBus.query<boolean>(
                    ACCOUNT_IS_PROCESSING_STATUS_QUERY,
                );

                if (isProcessing) {
                    notificationService.show(
                        this.$t('verification.notification.pending'),
                        {
                            closeDelay: NotificationDelay.LONG,
                            uniqueID: 'verification-notification-pending',
                        },
                    );
                    return;
                }

                notificationService.show(
                    this.$t('verification.notification.required'),
                    {
                        closeDelay: NotificationDelay.LONG,
                        uniqueID: 'verification-notification-required',
                    },
                );
            },
            { distinct: true },
        );
        window.eventBus.subscribe(ACCOUNT_IS_VERIFIED_NOTIFICATION, () => {
            notificationService.show(
                this.$t('account.verified-successfully.notification'),
                {
                    style: NotificationStyle.SUCCESS,
                    uniqueID: 'account.verified-successfully.notification',
                },
            );
        });
        window.eventBus.subscribe(
            ACCOUNT_IS_VERIFIEDPARTIAL_NOTIFICATION,
            () => {
                notificationService.show(
                    this.$t('account.verified-partially.notification'),
                    {
                        style: NotificationStyle.INFO,
                        uniqueID: 'account.verified-partially.notification',
                    },
                );
            },
        );

        this.userProfileUpdatedSubscription = magic.events.userProfileUpdated(
            async () => {
                const restrictionType =
                    typeof this.$route.meta.corexUserRestriction === 'function'
                        ? this.$route.meta.corexUserRestriction(this.$route)
                        : this.$route.meta.corexUserRestriction;

                const user = await window.queryBus.query<IUserProfile>(
                    USER_PROFILE_DATA,
                );

                const restrictionCheck = getRestrictionCheck(
                    user!,
                    restrictionType,
                );
                if (restrictionType) {
                    if (restrictionCheck.condition && !isModalOpen()) {
                        openModal(restrictionCheck.content);
                        return this.$router.push({ name: RouteName.HOME });
                    }
                }

                if (
                    (this.$route.name === RouteName.PROMOTIONS ||
                        this.$route.name === RouteName.REWARDS) &&
                    !user.compliance.isPromotionsAllowed
                ) {
                    openModal('promotions-restricted-modal.description');
                    this.$router.push({ name: RouteName.HOME });
                    return;
                }

                if (
                    this.$route.path.includes(RouteName.GAMES) &&
                    !user.compliance.isWageringAllowed
                ) {
                    window.commandBus.publish(CLOSE_GAME);
                    openModal('wagering-restricted-modal.description');
                    this.$router.push({ name: RouteName.HOME });
                    return;
                }

                if (
                    this.checkIsWalletOpen(WalletViewRouteName.DEPOSIT) &&
                    !user.compliance.isDepositAllowed &&
                    config.sweepstakes
                ) {
                    const restriction = getRestrictionCheck(
                        user!,
                        CorexUserRestriction.DEPOSIT,
                    );
                    openModal(restriction.content);
                    return this.$router.push({ name: RouteName.HOME });
                }

                if (
                    this.checkIsWalletOpen(WalletViewRouteName.WITHDRAW) &&
                    !user.compliance.isWithdrawAllowed &&
                    config.sweepstakes
                ) {
                    const restriction = getRestrictionCheck(
                        user!,
                        CorexUserRestriction.WITHDRAW,
                    );
                    openModal(restriction.content);
                    return this.$router.push({ name: RouteName.HOME });
                }
            },
        );
    }

    checkIsWalletOpen(routeName: string) {
        const queryModalPath = this.$route.query.modal;
        return (
            (Array.isArray(queryModalPath) &&
                queryModalPath.some((path) => path?.includes(routeName))) ||
            !!queryModalPath?.includes(routeName)
        );
    }

    destroyed() {
        this.blockStatusSubscription?.unsubscribe();
        if (this.userProfileUpdatedSubscription) {
            this.userProfileUpdatedSubscription.unsub();
            this.userProfileUpdatedSubscription = undefined;
        }
    }
}
